import authApiService from "./userApiService"; // Ensure this is your axios instance with interceptors

// Helper function to handle errors
const handleError = (error) => {
  console.error('API Error:', error);
  return {
    success: false,
    error: error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred',
    statusCode: error.response?.status,
    details: error.response?.data?.details || null
  };
};

// Helper function to handle successful responses
const handleSuccess = (response, message = null) => {
  return {
    success: true,
    data: response.data,
    message: message || response.data?.message || 'Operation completed successfully',
    statusCode: response.status
  };
};

export const tournamentHouseFeeService = {
  // ============================================================================
  // EMPLOYEE OPERATIONS
  // ============================================================================

  // Get all employees
  async getEmployees() {
    try {
      const response = await authApiService.get("/tournament-house-fees/employees");
      return handleSuccess(response, 'Employees retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // TRANSACTION CRUD OPERATIONS (RESTful)
  // ============================================================================

  // Get all transactions
  async getAllTransactions() {
    try {
      const response = await authApiService.get("/tournament-house-fees/transactions");
      return handleSuccess(response, 'Tournament house fee transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction by ID
  async getTransactionById(id) {
    try {
      const response = await authApiService.get(`/tournament-house-fees/transactions/${id}`);
      return handleSuccess(response, 'Tournament house fee transaction retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Add new transaction
  async addTransaction(transactionData) {
    try {
      const payload = {
        transactionType: transactionData.transactionType,
        amount: parseFloat(transactionData.amount),
        notes: transactionData.notes || ''
      };

      const response = await authApiService.post("/tournament-house-fees/transactions", payload);
      return handleSuccess(response, 'Tournament house fee transaction added successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Update transaction by ID
  async updateTransaction(id, transactionData) {
    try {
      const payload = {
        date: transactionData.date,
        notes: transactionData.notes,
      };

      const response = await authApiService.put(`/tournament-house-fees/transactions/${id}`, payload);
      return handleSuccess(response, 'Tournament house fee transaction updated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete transaction by ID
  async deleteTransaction(id) {
    try {
      const response = await authApiService.delete(`/tournament-house-fees/transactions/${id}`);
      return handleSuccess(response, 'Tournament house fee transaction deleted successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // BALANCE OPERATIONS
  // ============================================================================

  // Get current balance
  async getCurrentBalance() {
    try {
      const response = await authApiService.get("/tournament-house-fees/balance");
      return handleSuccess(response, 'Tournament house fee balance retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // VERIFICATION OPERATIONS
  // ============================================================================

  // Verify balance (Admin/Management only)
  async verifyBalance(verificationData) {
    try {
      const payload = {
        amount: parseFloat(verificationData.amount)
      };

      const response = await authApiService.post("/tournament-house-fees/verify", payload);
      return handleSuccess(response, 'Tournament house fee balance verification completed');
    } catch (error) {
      return handleError(error);
    }
  },

  // Check verification status
  async checkVerificationStatus() {
    try {
      const response = await authApiService.get("/tournament-house-fees/verification-status");
      return handleSuccess(response, 'Tournament house fee verification status retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await authApiService.get("/tournament-house-fees/health");
      return handleSuccess(response, 'Tournament house fee service is healthy');
    } catch (error) {
      return handleError(error);
    }
  },

  async getTransactions() {
    try {
      const response = await authApiService.get("/tournament-house-fees/transactions");
      return handleSuccess(response, 'Tournament house fee transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async getBalance() {
    try {
      const response = await authApiService.get("/tournament-house-fees/balance");
      return handleSuccess(response, 'Tournament house fee balance retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async createTransaction(transactionData) {
    try {
      const payload = {
        transactionType: transactionData.transactionType,
        amount: parseFloat(transactionData.amount),
        notes: transactionData.notes || '',
        name: transactionData.name || '',
      };

      const response = await authApiService.post("/tournament-house-fees/transactions", payload);
      return handleSuccess(response, 'Tournament house fee transaction created successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async editTransaction(id, transactionData) {
    try {
      const payload = {
        date: transactionData.date,
        name: transactionData.name,
        notes: transactionData.notes,
      };

      const response = await authApiService.put(`/tournament-house-fees/transactions/${id}`, payload);
      return handleSuccess(response, 'Tournament house fee transaction edited successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  validateTransactionData(transactionData) {
    const errors = [];
    
    if (!transactionData.transactionType) {
      errors.push('Transaction type is required');
    } else if (!['credit', 'debit'].includes(transactionData.transactionType)) {
      errors.push('Transaction type must be either "credit" or "debit"');
    }
    
    if (!transactionData.amount) {
      errors.push('Amount is required');
    } else if (isNaN(parseFloat(transactionData.amount))) {
      errors.push('Amount must be a valid number');
    } else if (parseFloat(transactionData.amount) <= 0) {
      errors.push('Amount must be greater than 0');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  // Format transaction data for API
  formatTransactionData(transactionData) {
    return {
      transactionType: transactionData.transactionType,
      amount: parseFloat(transactionData.amount),
      notes: transactionData.notes?.trim() || '',
      name: transactionData.name?.trim() || '',
      date: transactionData.date || new Date().toISOString()
    };
  },

  // Calculate transaction totals
  calculateTransactionTotals(transactions) {
    return transactions.reduce((totals, transaction) => {
      const amount = parseFloat(transaction.transaction_amount || 0);
      
      if (transaction.transaction_type === 'credit') {
        totals.totalCredits += amount;
        totals.creditCount += 1;
      } else if (transaction.transaction_type === 'debit') {
        totals.totalDebits += amount;
        totals.debitCount += 1;
      }
      
      return totals;
    }, {
      totalCredits: 0,
      totalDebits: 0,
      creditCount: 0,
      debitCount: 0,
      netAmount: 0
    });
  },

  // Get transactions with filters and pagination
  async getFilteredTransactions(filters = {}, pagination = {}) {
    try {
      const params = {};
      
      // Filter parameters
      if (filters.transactionType) params.transactionType = filters.transactionType;
      if (filters.name) params.name = filters.name;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.addedBy) params.addedBy = filters.addedBy;
      if (filters.minAmount) params.minAmount = filters.minAmount;
      if (filters.maxAmount) params.maxAmount = filters.maxAmount;
      
      // Pagination parameters
      if (pagination.page) params.page = pagination.page;
      if (pagination.limit) params.limit = pagination.limit;
      if (pagination.sortBy) params.sortBy = pagination.sortBy;
      if (pagination.sortOrder) params.sortOrder = pagination.sortOrder;

      const response = await authApiService.get("/tournament-house-fees/transactions", { params });
      return handleSuccess(response, 'Filtered tournament house fee transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  }
};

export default tournamentHouseFeeService;
